import { ReactNode, useId } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { cn } from "../utls/utils";
import Typography from "./typography";
import { ModalPortal } from "./modal/modal-portal";

export type ThemedModalTone = "terracotta" | "neutral";

const containerToneClasses: Record<ThemedModalTone, string> = {
  terracotta: "border-terracotta-200",
  neutral: "border-beige-200",
};

const headerToneClasses: Record<ThemedModalTone, string> = {
  terracotta: "bg-cream-100",
  neutral: "bg-beige-50",
};

const titleToneClasses: Record<ThemedModalTone, string> = {
  terracotta: "text-terracotta-700",
  neutral: "text-brown-700",
};

const footerToneClasses: Record<ThemedModalTone, string> = {
  terracotta: "border-terracotta-100 bg-cream-50",
  neutral: "border-beige-200 bg-beige-50",
};

const closeButtonToneClasses: Record<ThemedModalTone, string> = {
  terracotta:
    "text-terracotta-500 hover:text-terracotta-700 focus-visible:ring-terracotta-500",
  neutral:
    "text-brown-500 hover:text-brown-700 focus-visible:ring-brown-500",
};

const iconToneClasses: Record<ThemedModalTone, string> = {
  terracotta: "bg-terracotta-50 text-terracotta-600",
  neutral: "bg-beige-100 text-brown-600",
};

export interface ThemedModalProps {
  /** Controls whether the modal is rendered. */
  isOpen: boolean;
  /** Callback fired when the modal requests to close. */
  onClose: () => void;
  /** Title rendered at the top of the modal. */
  title: ReactNode;
  /** Optional supporting copy that appears below the title. */
  description?: ReactNode;
  /** Primary content for the modal body. */
  children?: ReactNode;
  /** Optional footer actions rendered at the bottom of the modal. */
  footer?: ReactNode;
  /** Accent tone that controls border and header styling. */
  tone?: ThemedModalTone;
  /** Optional decorative icon rendered alongside the title. */
  icon?: ReactNode;
  /** Additional className overrides for the panel. */
  className?: string;
  /** Optional overrides for the modal portal container. */
  panelClassName?: string;
}

export function ThemedModal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  tone = "terracotta",
  icon,
  className,
  panelClassName,
}: ThemedModalProps) {
  const titleId = useId();
  const descriptionId = useId();

  if (!isOpen) {
    return null;
  }

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName={cn("bg-transparent shadow-none p-0 overflow-visible", panelClassName)}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          "relative w-full max-w-xl overflow-hidden rounded-2xl border bg-white shadow-2xl",
          containerToneClasses[tone],
          className
        )}
      >
        <header
          className={cn(
            "flex items-start justify-between gap-4 border-b px-6 py-5",
            headerToneClasses[tone],
            tone === "terracotta" ? "border-terracotta-100" : "border-beige-200"
          )}
        >
          <div className="flex flex-1 items-start gap-3">
            {icon ? (
              <span
                aria-hidden
                className={cn(
                  "mt-1 flex h-10 w-10 items-center justify-center rounded-full",
                  iconToneClasses[tone]
                )}
              >
                {icon}
              </span>
            ) : null}
            <div className="space-y-2">
              <Typography
                id={titleId}
                variant="h4"
                className={cn("leading-tight", titleToneClasses[tone])}
              >
                {title}
              </Typography>
              {description ? (
                <Typography
                  id={descriptionId}
                  variant="body2"
                  className="text-brown-600"
                >
                  {description}
                </Typography>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              closeButtonToneClasses[tone],
              "bg-white/70 hover:bg-white"
            )}
            aria-label="Close modal"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </header>
        {children ? (
          <div className="px-6 py-5 text-brown-700">{children}</div>
        ) : null}
        {footer ? (
          <footer
            className={cn(
              "flex flex-col gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-end",
              footerToneClasses[tone]
            )}
          >
            {footer}
          </footer>
        ) : null}
      </section>
    </ModalPortal>
  );
}

export default ThemedModal;
